import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import { useStyles } from "../MachineCenterCreationForm.styles";
import type { IMachineCenterCreationFormValues } from "../MachineCenterCreationFormInterfaces";
import { ProductList } from "./components";

export const PERFORMANCE_TAB_FIELD_NAMES: Array<keyof IMachineCenterCreationFormValues> = [
    "oee_input_method_id",
    "oee_products",
];

interface PerformanceTabProps {
    values: IMachineCenterCreationFormValues;
    errors: Record<string, unknown>;
    touched: Record<string, unknown>;
    setFieldValue: (field: string, value: unknown) => void;
    t: (key: string) => string;
    isMillAspenPlusEnabled: boolean;
}

export const PerformanceTab = ({
    values,
    errors,
    touched,
    setFieldValue,
    t,
    isMillAspenPlusEnabled,
}: PerformanceTabProps) => {
    const classes = useStyles();
    const { oeeInputMethodOptions, loading } = MachineCentersEntity.GetOeeInputMethods();
    const disabled = !isMillAspenPlusEnabled || !values.aspen_plus_oee_enabled;
    const productsError = touched.oee_products && typeof errors.oee_products === "string" ? errors.oee_products : null;
    const noMethodSelected = values.oee_input_method_id === "";

    return (
        <Box className={classes.tabPanel}>
            <Box className={classes.boxContainer}>
                <InputLabel id="oee-input-method-label">
                    <Typography variant="subtitle2" className={classes.formLabel}>
                        {t("corporate_backoffice.production_line_view.machine_center_creation_form.oee_input_method")}
                    </Typography>
                </InputLabel>
                <FormControl
                    error={Boolean(touched.oee_input_method_id && errors.oee_input_method_id)}
                    className={classes.textField}
                    fullWidth
                    disabled={disabled || loading}
                >
                    <Select
                        labelId="oee-input-method-label"
                        id="oee-input-method-select"
                        className={classes.select}
                        name="oee_input_method_id"
                        value={values.oee_input_method_id === "" ? "" : values.oee_input_method_id}
                        onChange={(e) =>
                            setFieldValue("oee_input_method_id", e.target.value === "" ? "" : Number(e.target.value))
                        }
                        displayEmpty
                    >
                        {oeeInputMethodOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                                {opt.name}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {touched.oee_input_method_id && errors.oee_input_method_id
                            ? t(String(errors.oee_input_method_id))
                            : null}
                    </FormHelperText>
                </FormControl>
            </Box>

            <Box className={classes.boxContainer}>
                <InputLabel>
                    <Typography variant="subtitle2" className={classes.formLabel}>
                        {t("corporate_backoffice.production_line_view.machine_center_creation_form.oee_products_title")}
                        {noMethodSelected && !disabled && (
                            <span
                                style={{ fontWeight: 400, textTransform: "none", fontSize: "11px", marginLeft: "4px" }}
                            >
                                {t(
                                    "corporate_backoffice.production_line_view.machine_center_creation_form.oee_products_select_method_hint",
                                )}
                            </span>
                        )}
                    </Typography>
                </InputLabel>
                <ProductList
                    products={values.oee_products}
                    setFieldValue={setFieldValue}
                    disabled={disabled || noMethodSelected}
                    t={t}
                    selectedMethodId={values.oee_input_method_id}
                />
                {productsError && <FormHelperText error>{t(productsError)}</FormHelperText>}
            </Box>
        </Box>
    );
};
